import React from 'react';
import { Paragraph } from 'common-components/typography';
import { Avatar, Row } from 'antd';
import { HyperlinkButton, PrimaryButton, SecondaryButton } from 'common-components/buttons';
import _ from 'lodash';
import moment from 'moment-timezone';
import { Text } from '@good/ui/core';
import { Worker } from './types';

interface AssignWorkerStep14Props {
  _showUnavailableBookings: () => void;
  displayTimezone: string | undefined;
  isShiftStatusPending: boolean;
  onClose: () => void;
  onSubmitAssign: (onBehalf: boolean) => Promise<void>;
  scheduledRemoveConflict: unknown | null;
  selectedWorker: Worker | null;
  showUnavailableBookings: boolean;
}

export const AssignWorkerStep14 = ({
  displayTimezone,
  selectedWorker,
  scheduledRemoveConflict,
  isShiftStatusPending,
  showUnavailableBookings,
  _showUnavailableBookings,
  onClose,
  onSubmitAssign,
}: AssignWorkerStep14Props) => {
  const removalDate = scheduledRemoveConflict ? scheduledRemoveConflict.removedOn : '';
  const sessions = scheduledRemoveConflict
    ? scheduledRemoveConflict.sessions || scheduledRemoveConflict.shiftSlots
    : [];
  const name = selectedWorker ? `${selectedWorker.firstName} ${selectedWorker.lastName}` : '';

  return (
    <>
      <Paragraph>
        The selected team member is unavailable for some booking you are attempting to assign them to. The team member
        will not be assigned to the following
      </Paragraph>
      <div
        className='p-medium bordered border-standard-gray rounded-big bg-quaternary line-height-135 flex-column'
        style={{ maxHeight: '300px', overflow: 'auto' }}
      >
        <div className='flex-row'>
          <div className='bg-red-lightest text-color-red text-weight-bold'>TEAM MEMBER SCHEDULED TO LEAVE COMPANY</div>
          <div className='ml-large flex-row'>
            <Avatar icon='user' size={25} shape='square' src={selectedWorker?.attachmentUrl} />
            <b className='ml-small'>
              <Text className='text-color-tertiary text-size-regular'>{name}</Text>
            </b>
          </div>
        </div>
        <Paragraph>
          <Text weight='bold'>{name}</Text> is scheduled to leave the company on{' '}
          {moment.tz(removalDate, displayTimezone).format('Do of MMMM')} and will not be assigned to the following{' '}
          <Text weight='bold'>
            {sessions.length} booking{sessions.length > 1 ? 's' : ''}
          </Text>
        </Paragraph>
        <HyperlinkButton onClick={_showUnavailableBookings}>
          {showUnavailableBookings ? 'Hide bookings...' : 'Show bookings...'}
        </HyperlinkButton>
        {showUnavailableBookings && (
          <div className='mt-large'>
            {sessions.map((session) => (
              <Paragraph key={session.serviceDateTimeId}>
                <Text weight='bold'>{moment.tz(session.startDateTime, displayTimezone).format('DD MMMM YYYY')}</Text>
                <Text className='ml-small'>
                  {moment.tz(session.startDateTime, displayTimezone).format('HH:mm A')} -{' '}
                  {moment.tz(session.endDateTime, displayTimezone).format('HH:mm A')}
                </Text>
              </Paragraph>
            ))}
          </div>
        )}
      </div>
      <div className='mt-x3-large'>
        <Row type={'flex'} justify={'end'}>
          <SecondaryButton size='large' onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            size='large'
            className='ml-small'
            onClick={async () => await onSubmitAssign(!isShiftStatusPending)}
          >
            Continue
          </PrimaryButton>
        </Row>
      </div>
    </>
  );
};
